<template>
  <div class="servicos-categorias-form">
    <form @submit.prevent="handleSubmit">
      <div class="flex-container">
        <!-- Nome da Categoria -->
        <div class="flex-item-full">
          <div class="form-group" :class="{ 'has-error': errors.servicoscategoria_nome }">
            <label>{{ t('Nome') }} *</label>
            <input-text
              v-model="formData.servicoscategoria_nome"
              elem-id="servicos_categorias_servicoscategoria_nome"
              :placeholder="t('Nome')"
              maxlength="100"
              class="w-full"
              data-testid="servicos-categorias-nome"
            />
            <span v-if="errors.servicoscategoria_nome" class="help-block">{{ errors.servicoscategoria_nome }}</span>
          </div>
        </div>
      </div>

      <div class="flex-container">
        <!-- Prazo de Retorno -->
        <div class="flex-item-half">
          <div class="form-group" :class="{ 'has-error': errors.servicoscategoria_prazo_retorno_dias }">
            <label>{{ t('Prazo de Retorno (dias)') }} *</label>
            <input-number
              v-model="formData.servicoscategoria_prazo_retorno_dias"
              elem-id="servicos_categorias_servicoscategoria_prazo_retorno_dias"
              :placeholder="t('Prazo de Retorno (dias)')"
              :min="0"
              class="w-full"
              data-testid="servicos-categorias-prazo-retorno"
            />
            <span v-if="errors.servicoscategoria_prazo_retorno_dias" class="help-block">{{ errors.servicoscategoria_prazo_retorno_dias }}</span>
          </div>
        </div>

        <!-- Prazo de Garantia -->
        <div class="flex-item-half">
          <div class="form-group" :class="{ 'has-error': errors.servicoscategoria_prazo_garantia_dias }">
            <label>{{ t('Prazo de Garantia (dias)') }} *</label>
            <input-number
              v-model="formData.servicoscategoria_prazo_garantia_dias"
              elem-id="servicos_categorias_servicoscategoria_prazo_garantia_dias"
              :placeholder="t('Prazo de Garantia (dias)')"
              :min="0"
              class="w-full"
              data-testid="servicos-categorias-prazo-garantia"
            />
            <span v-if="errors.servicoscategoria_prazo_garantia_dias" class="help-block">{{ errors.servicoscategoria_prazo_garantia_dias }}</span>
          </div>
        </div>
      </div>

      <div class="flex-container">
        <!-- Produtos Associados -->
        <div class="flex-item-full">
          <div class="form-group" :class="{ 'has-error': errors.servicoscategoria_produtos_associados }">
            <label>{{ t('Produtos Associados') }} *</label>
            <select-list
              v-model="formData.servicoscategoria_produtos_associados"
              elem-id="servicos_categorias_servicoscategoria_produtos_associados"
              :itens-select="produtosAssociadosOptions"
              class="w-full"
              data-testid="servicos-categorias-produtos-associados"
            />
            <span v-if="errors.servicoscategoria_produtos_associados" class="help-block">{{ errors.servicoscategoria_produtos_associados }}</span>
          </div>
        </div>
      </div>

    </form>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { log } from '../../utils/DebugLogger';
import { eventBus } from '../../eventBus';
import { useServicosCategorias } from '../../composables/useServicosCategorias';

interface CategoriaFormData {
  servicoscategoria_id: number | null;
  servicoscategoria_nome: string;
  servicoscategoria_prazo_retorno_dias: number;
  servicoscategoria_prazo_garantia_dias: number;
  servicoscategoria_produtos_associados: number;
}

interface SelectOption {
  ID: number;
  Name: string;
}

const { t } = useI18n();
const {
  fetchCategorias,
  createCategoria,
  updateCategoria
} = useServicosCategorias();

const props = defineProps<{
  categoriaId?: number | string | null;
}>();

const emit = defineEmits<{
  saved: [];
  cancel: [];
}>();

const formData = reactive<CategoriaFormData>({
  servicoscategoria_id: null,
  servicoscategoria_nome: '',
  servicoscategoria_prazo_retorno_dias: 0,
  servicoscategoria_prazo_garantia_dias: 0,
  servicoscategoria_produtos_associados: 0
});

const errors = reactive<Record<string, string>>({});
const isSaving = ref<boolean>(false);

const produtosAssociadosOptions = computed<SelectOption[]>(() => [
  { ID: 1, Name: t('Sim') },
  { ID: 0, Name: t('Não') }
]);

const canSave = computed<boolean>(() => {
  return formData.servicoscategoria_nome.trim() !== '' &&
         formData.servicoscategoria_prazo_retorno_dias >= 0 &&
         formData.servicoscategoria_prazo_garantia_dias >= 0;
});

const initializeForNew = async (): Promise<void> => {
  log('ServicosCategoriasForm', 'initializeForNew', 'Initializing form for new categoria');

  formData.servicoscategoria_id = null;
  formData.servicoscategoria_nome = '';
  formData.servicoscategoria_prazo_retorno_dias = 0;
  formData.servicoscategoria_prazo_garantia_dias = 0;
  formData.servicoscategoria_produtos_associados = 0;

  Object.keys(errors).forEach(key => delete errors[key]);
};

const loadForEdit = async (): Promise<void> => {
  if (!props.categoriaId) return;

  log('ServicosCategoriasForm', 'loadForEdit', 'Loading categoria', props.categoriaId);

  try {
    const categorias = await fetchCategorias();
    const data = categorias.find((c: CategoriaFormData) => c.servicoscategoria_id == props.categoriaId);

    if (!data) {
      throw new Error('Categoria não encontrada');
    }

    log('ServicosCategoriasForm', 'loadForEdit', 'Loaded categoria', data);

    Object.assign(formData, data);
  } catch (error) {
    log('ServicosCategoriasForm', 'loadForEdit', 'Error loading categoria', error);
    eventBus.emit('globalMessage.error', t('Erro ao carregar categoria'));
  }
};

const validateForm = (): boolean => {
  Object.keys(errors).forEach(key => delete errors[key]);

  let isValid = true;

  if (!formData.servicoscategoria_nome.trim()) {
    errors.servicoscategoria_nome = t('Nome é obrigatório');
    isValid = false;
  }

  if (formData.servicoscategoria_prazo_retorno_dias < 0) {
    errors.servicoscategoria_prazo_retorno_dias = t('Prazo não pode ser negativo');
    isValid = false;
  }

  if (formData.servicoscategoria_prazo_garantia_dias < 0) {
    errors.servicoscategoria_prazo_garantia_dias = t('Prazo não pode ser negativo');
    isValid = false;
  }

  return isValid;
};

const handleSubmit = async (): Promise<void> => {
  log('ServicosCategoriasForm', 'handleSubmit', 'Submitting form', formData);

  if (!validateForm()) {
    log('ServicosCategoriasForm', 'handleSubmit', 'Form validation failed');
    return;
  }

  isSaving.value = true;

  try {
    if (formData.servicoscategoria_id) {
      await updateCategoria(formData.servicoscategoria_id, formData);
      log('ServicosCategoriasForm', 'handleSubmit', 'Categoria updated successfully');
      eventBus.emit('globalMessage.success', t('Categoria atualizada com sucesso'));
    } else {
      await createCategoria(formData);
      log('ServicosCategoriasForm', 'handleSubmit', 'Categoria created successfully');
      eventBus.emit('globalMessage.success', t('Categoria criada com sucesso'));
    }

    emit('saved');
  } catch (error) {
    log('ServicosCategoriasForm', 'handleSubmit', 'Error saving categoria', error);
    eventBus.emit('globalMessage.error', t('Erro ao salvar categoria'));
  } finally {
    isSaving.value = false;
  }
};

const handleCancel = (): void => {
  log('ServicosCategoriasForm', 'handleCancel', 'Form cancelled');
  emit('cancel');
};

watch(() => props.categoriaId, async (newId, oldId) => {
  log('ServicosCategoriasForm', 'watch:categoriaId', 'categoriaId changed', { oldId, newId });

  if (newId) {
    await loadForEdit();
  } else if (oldId !== null) {
    await initializeForNew();
  }
});

onMounted(async () => {
  log('ServicosCategoriasForm', 'mounted', 'Component mounted');

  if (props.categoriaId) {
    await loadForEdit();
  } else {
    await initializeForNew();
  }
});

defineExpose({
  initializeForNew,
  loadForEdit,
  handleSubmit,
  canSave
});
</script>

<style scoped>
.servicos-categorias-form {
  padding: 15px;
}

.flex-container {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-bottom: 20px;
}

.flex-item-half {
  flex: 1 1 calc(50% - 8px);
  min-width: 200px;
}

.flex-item-full {
  flex: 1 1 100%;
}

.w-full {
  width: 100%;
}

.form-group {
  margin-bottom: 15px;
}

.has-error .help-block {
  color: #dd4b39;
}
</style>
